import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  primaryColors,
  whiteShades
} from '@assets/styles/helpers/colors.styles'
import { Checkbox, Chip, ListItem, Typography } from '@mui/material'
import { selectValueType } from '@/shared/components/DynamicForm/DynamicForm'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { forwardRef, useState } from 'react'
import {
  chipStyles,
  listboxStyles,
  listElementStyles,
  popperStyles,
  selectStyles
} from './Select.styles'
import { inputStyles } from '../TextField/TextField.styles'
import styles from './Select.module.scss'

interface SelectProps {
  field?: {
    name: string
  }
  label?: string
  placeholder?: string
  options: OptionType[]
  hasError?: boolean
  helperText?: string
  disabled?: boolean
  focused?: boolean
  isMutiSelect?: boolean
  flexGrow?: number
  onBlur?: () => void
  value?: selectValueType
  hideClearIcon?: boolean
  widthFitContent?: boolean
  onChange?: (value: selectValueType) => void
  defaultValue?: selectValueType
  onOpen?: () => void
  onClose?: () => void
  minWidth?: string
  maxWidth?: string
  readonly?: boolean
  required?: boolean
  /**
   * Values that must stay selected. Their chips lose the delete icon, their
   * options cannot be toggled off, and anything that clears the field wholesale
   * (the clear icon, backspace) puts them straight back.
   */
  lockedValues?: string[]
  /**
   * Keeps every visible chip whole in a narrow field. They stay on one row and
   * give way to each other, letting the label ellipsis do the shortening, so
   * none is left sliced in half by the field's edge.
   */
  fitTags?: boolean
}

const Select = forwardRef<HTMLDivElement, SelectProps>((props, ref) => {
  const {
    label,
    placeholder,
    options,
    hasError,
    helperText,
    disabled,
    field,
    focused,
    isMutiSelect,
    flexGrow,
    value,
    onBlur,
    hideClearIcon,
    widthFitContent,
    onChange,
    defaultValue,
    onOpen,
    onClose,
    minWidth,
    maxWidth,
    readonly = false,
    required = false,
    lockedValues,
    fitTags = false
  } = props

  // Collapsing the chips to "first N + more" is normally MUI's `limitTags`,
  // but that only kicks in when renderTags returns an array, and the chips
  // need a wrapper element of their own to scroll in. So the same rule is
  // applied by hand below.
  const tagsLimit = isMutiSelect ? 2 : -1

  const hasLocked = !!lockedValues?.length
  const isLocked = (option: OptionType) =>
    hasLocked && lockedValues!.includes(option.value as string)
  const [isFocused, setIsFocused] = useState(false)

  // With `multiple`, the chips are separate elements and the text input stays
  // EMPTY behind them, so its placeholder keeps rendering next to a selection,
  // squeezed into whatever width the chips leave over ("Wybierz Użyt…"). Drop it
  // as soon as there is a value. Single-select is unaffected either way (the
  // input holds the label, so the browser hides the placeholder itself).
  const hasValue = Array.isArray(value) ? value.length > 0 : value != null

  return (
    <Autocomplete
      id={field?.name}
      onFocus={() => setIsFocused(true)}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setIsFocused(false)
        }
      }}
      options={options}
      size="small"
      multiple={isMutiSelect}
      disableCloseOnSelect={isMutiSelect}
      renderOption={(props, option: OptionType, { selected }) => (
        <ListItem {...props} sx={listElementStyles}>
          {isMutiSelect && (
            <Checkbox style={{ marginRight: 8 }} checked={selected} />
          )}
          {option.iconImage && (
            <img
              className={styles.optionIconImage}
              src={option.iconImage}
              alt="option-icon"
            />
          )}
          <Typography className="body-bold-medium">{option.label}</Typography>
          {option.icon && <i className={option.icon} />}
        </ListItem>
      )}
      getOptionLabel={option => option.label || ''}
      getOptionDisabled={hasLocked ? isLocked : undefined}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      value={value}
      defaultValue={defaultValue}
      disabled={disabled}
      onChange={(_, newValue) => {
        if (onChange) {
          if (isMutiSelect) {
            let nextValue = Array.isArray(newValue) ? [...newValue] : newValue

            // `getOptionDisabled` only guards the list. The clear icon and
            // backspace go straight through it, and clearing the field is
            // exactly the case a locked value exists to survive.
            if (hasLocked && Array.isArray(nextValue)) {
              const present = new Set(nextValue.map(option => option.value))
              // Put back from what was selected a moment ago, never from
              // `options`. A locked value is by definition already selected,
              // while the option list can still be loading or filtered, and
              // rebuilding from there let the lock fail silently in exactly the
              // state it exists to survive.
              const previous = Array.isArray(value) ? value : []
              const restored = previous.filter(
                option => isLocked(option) && !present.has(option.value)
              )
              nextValue = [...nextValue, ...restored]
            }

            const sortedNewValue = Array.isArray(nextValue)
              ? nextValue.sort((a, b) => a.label.localeCompare(b.label))
              : nextValue
            onChange(sortedNewValue)
          } else {
            onChange(newValue)
          }
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={required && label ? `${label} *` : label}
          placeholder={hasValue ? undefined : placeholder}
          variant="outlined"
          rows={1}
          inputProps={{
            ...params.inputProps,
            ...(readonly && {
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key.length === 1) e.preventDefault()
              },
              style: {
                ...((params.inputProps as unknown as Record<string, unknown>)
                  ?.style as object),
                caretColor: 'transparent',
                cursor: 'pointer'
              }
            })
          }}
          sx={{
            ...inputStyles,
            width: widthFitContent
              ? 'fit-content !important'
              : '100% !important',
            '.MuiInputBase-root.MuiOutlinedInput-root': {
              paddingRight: widthFitContent
                ? '1.75rem !important'
                : '4rem !important',
              maxHeight: '9rem',
              overflowY: 'auto',
              minWidth: minWidth || '100%',
              maxWidth: maxWidth || '100%'
            }
          }}
          helperText={helperText}
          error={hasError}
          onBlur={onBlur}
          focused={focused}
        />
      )}
      sx={{
        ...selectStyles,
        flexGrow,
        '&.MuiAutocomplete-root': {
          width: widthFitContent ? 'fit-content' : '100%',
          maxWidth: maxWidth || '100%'
        }
      }}
      clearIcon={
        !hideClearIcon &&
        value && (
          <CloseOutlinedIcon
            sx={{ color: `${whiteShades.shade1} !important` }}
            fontSize="small"
          />
        )
      }
      popupIcon={<ExpandMoreIcon sx={{ color: primaryColors.white }} />}
      slotProps={{
        paper: {
          sx: popperStyles
        },
        listbox: {
          sx: listboxStyles
        }
      }}
      renderTags={(value, getTagProps) => {
        /*
          Focusing normally expands the strip to every chip, which is fine when
          it can scroll. A field that has asked not to clip has nowhere to put
          them, and they ended up fighting over one row, so there the collapse
          stays on and the dropdown's own ticks show the full selection.
        */
        const isCollapsed =
          tagsLimit > -1 && value.length > tagsLimit && (fitTags || !isFocused)
        const visibleTags = isCollapsed ? value.slice(0, tagsLimit) : value
        const hiddenCount = value.length - visibleTags.length

        return (
          <div
            className={`${styles.tagScroller} ${
              fitTags ? styles.tagScrollerFitted : ''
            }`}
            // Vertical wheel scrolls the strip sideways, without it the chips
            // beyond the right edge are unreachable on a mouse.
            onWheel={event => {
              const strip = event.currentTarget
              if (strip.scrollWidth > strip.clientWidth) {
                strip.scrollLeft += event.deltaY
              }
            }}
          >
            {visibleTags.map((option: OptionType, index) => {
              // Dropping `onDelete` is what removes the icon; MUI renders none
              // when the handler is absent.
              const { onDelete, ...tagProps } = getTagProps({ index })

              return (
                <Chip
                  {...tagProps}
                  {...(isLocked(option) ? {} : { onDelete })}
                  label={option.label}
                  sx={chipStyles}
                  deleteIcon={<CancelIcon fontSize="small" />}
                />
              )
            })}
            {hiddenCount > 0 && (
              <span className="MuiAutocomplete-tag">{`+${hiddenCount}`}</span>
            )}
          </div>
        )
      }}
      onOpen={onOpen}
      onClose={onClose}
      ref={ref}
    />
  )
})

export default Select
